import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import Layout from "./components/Layout/Layout";
import Login from "./pages/Auth/Login";
import Dashboard from "./pages/Dashboard/Dashboard";
import Families from "./pages/Families/Families";
import FamilyEdit from "./pages/Families/FamilyEdit";
import Submissions from "./pages/Submissions/Submissions";
import Forms from "./pages/Forms/Forms";
import Documents from "./pages/Documents/Documents";
import Policies from "./pages/Policies/Policies";
import Handbook from "./pages/Handbook/Handbook";
import Centres from "./pages/Centres/Centres";
import Users from "./pages/Users/Users";
import Roles from "./pages/Roles/Roles";
import RolePermissions from "./pages/Roles/RolePermissions";
import ActivityLog from "./pages/ActivityLog/ActivityLog";
import Settings from "./pages/Settings/Settings";
import MyProfile from "./pages/Profile/MyProfile";
import NotFound from "./pages/NotFound";

import { useAuthStore } from "./store/authStore";
import { usePermission } from "./hooks/usePermission";

function RequireAuth({ children }) {
  const token =
    useAuthStore((s) => s.token) || localStorage.getItem("authToken");
  if (!token) return <Navigate to="/login" replace />;
  return children;
}

const denied = <Navigate to="/dashboard" replace />;

function NoAccessCard() {
  return (
    <div className="card mb-4">
      <div className="card-body text-center py-5 text-muted">
        <i className="cil-lock-locked d-block mb-2" style={{ fontSize: "1.75rem" }} />
        You don't have access to any screens. Please contact an administrator.
      </div>
    </div>
  );
}

function AppRoutes() {
  const { can } = usePermission();

  return (
    <Routes>
      <Route path="/login" element={<Login />} />

      <Route
        element={
          <RequireAuth>
            <Layout />
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />

        <Route
          path="dashboard"
          element={can("dashboard") ? <Dashboard /> : <NoAccessCard />}
        />

        <Route path="registrations" element={<Navigate to="/families" replace />} />
        <Route
          path="families"
          element={
            can("families") || can("registrations") || can("children") ? (
              <Families />
            ) : (
              denied
            )
          }
        />
        <Route
          path="families/:id/edit"
          element={can("families", "is_update") ? <FamilyEdit /> : denied}
        />
        <Route path="children" element={<Navigate to="/families" replace />} />
        <Route
          path="submissions"
          element={can("submissions") ? <Submissions /> : denied}
        />
        <Route path="forms" element={can("forms") ? <Forms /> : denied} />
        <Route path="documents" element={can("documents") ? <Documents /> : denied} />
        <Route path="policies" element={can("policies") ? <Policies /> : denied} />
        <Route path="handbook" element={can("handbook") ? <Handbook /> : denied} />
        <Route path="centres" element={can("centres") ? <Centres /> : denied} />
        <Route path="users" element={can("users") ? <Users /> : denied} />
        <Route path="roles" element={can("roles") ? <Roles /> : denied} />
        <Route
          path="roles/:id/permissions"
          element={can("roles", "is_update") ? <RolePermissions /> : denied}
        />
        <Route path="activity" element={can("activity") ? <ActivityLog /> : denied} />
        <Route path="settings" element={can("settings") ? <Settings /> : denied} />

        <Route path="my-profile" element={<MyProfile />} />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default function App() {
  // Global form validation feedback: any submitted form gets red borders on
  // invalid fields; they clear as the user fixes each field.
  useEffect(() => {
    const fieldSelector = "input, select, textarea";

    const onSubmitCapture = (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      form.classList.add("was-validated");
      form.querySelectorAll(fieldSelector).forEach((el) => {
        if (el.willValidate && !el.checkValidity()) {
          el.classList.add("is-invalid");
        } else {
          el.classList.remove("is-invalid");
        }
      });
    };

    const onInputCapture = (e) => {
      const el = e.target;
      if (
        !(el instanceof HTMLInputElement) &&
        !(el instanceof HTMLSelectElement) &&
        !(el instanceof HTMLTextAreaElement)
      ) {
        return;
      }
      if (el.validity.customError) {
        el.setCustomValidity("");
      }
      if (el.classList.contains("is-invalid") && el.checkValidity()) {
        el.classList.remove("is-invalid");
      }
    };

    document.addEventListener("submit", onSubmitCapture, true);
    document.addEventListener("input", onInputCapture, true);
    document.addEventListener("change", onInputCapture, true);
    return () => {
      document.removeEventListener("submit", onSubmitCapture, true);
      document.removeEventListener("input", onInputCapture, true);
      document.removeEventListener("change", onInputCapture, true);
    };
  }, []);

  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}
